import React from 'react';
import { Skeleton } from '@mui/material';
import { MuiPaper } from '@ace/ace-ui-web';
import styles from './SkeletonDataGrid.module.css';

interface SkeletonDataGridProps {
  /**
   * Number of skeleton tabs to render
   * @default 4
   */
  tabCount?: number;
  /**
   * Number of skeleton table rows to render
   * @default 8
   */
  rowCount?: number;
  /**
   * Number of table columns to render
   * @default 7
   */
  columnCount?: number;
}

/**
 * SkeletonDataGrid Component - Displays loading skeleton for MUI Paper with DataGrid
 * Mimics the structure of tabs, header, and data table while data loads
 * Can be reused across different pages that use tabbed data grids
 */
const SkeletonDataGrid: React.FC<SkeletonDataGridProps> = ({
  tabCount = 4,
  rowCount = 8,
  columnCount = 7,
}) => {
  return (
    <MuiPaper
      elevation={2}
      sx={{
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        margin: '0 auto',
        paddingRight: '50px',
      }}>
      <div className={styles.container}>
        {/* Skeleton Navigation Tabs */}
        <div className={styles.tabsContainer}>
          {[...Array(tabCount)].map((_, index) => (
            <Skeleton
              key={`tab-${index}`}
              variant="text"
              width={120}
              height={48}
              sx={{
                marginRight: '24px',
                borderRadius: '4px 4px 0 0',
              }}
            />
          ))}
        </div>

        {/* Skeleton Section */}
        <div className={styles.gridSection}>
          {/* Skeleton Header */}
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <Skeleton
                variant="text"
                width={200}
                height={28}
                sx={{ marginBottom: '4px' }}
              />
              <Skeleton
                variant="text"
                width={150}
                height={20}
              />
            </div>

            {/* Header Controls */}
            <div className={styles.headerControls}>
              <Skeleton
                variant="rectangular"
                width={180}
                height={40}
                sx={{ borderRadius: '4px', marginRight: '16px' }}
              />
            </div>
          </div>

          {/* Skeleton Data Table */}
          <div className={styles.tableContainer}>
            {/* Table Header */}
            <div className={styles.tableHeader}>
              {[...Array(columnCount)].map((_, colIndex) => (
                <div
                  key={`header-${colIndex}`}
                  className={styles.headerCell}>
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={20}
                  />
                </div>
              ))}
            </div>

            {/* Table Rows */}
            {[...Array(rowCount)].map((_, rowIndex) => (
              <div
                key={`row-${rowIndex}`}
                className={styles.tableRow}>
                {[...Array(columnCount)].map((_, colIndex) => (
                  <div
                    key={`cell-${rowIndex}-${colIndex}`}
                    className={styles.tableCell}>
                    {colIndex === 0 ? (
                      // First column - KPI names (wider text)
                      <Skeleton
                        variant="text"
                        width="90%"
                        height={16}
                      />
                    ) : colIndex === 1 ? (
                      // More Info column (link)
                      <Skeleton
                        variant="text"
                        width={60}
                        height={16}
                      />
                    ) : colIndex === 3 ? (
                      // Take Action column (button)
                      <Skeleton
                        variant="rectangular"
                        width={120}
                        height={32}
                        sx={{ borderRadius: '4px' }}
                      />
                    ) : colIndex === 6 ? (
                      // Last column (icon/badge)
                      <Skeleton
                        variant="circular"
                        width={24}
                        height={24}
                      />
                    ) : (
                      // Regular data columns
                      <Skeleton
                        variant="text"
                        width="70%"
                        height={16}
                      />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </MuiPaper>
  );
};

export default SkeletonDataGrid;
